import { useState, useEffect } from "react";
import { withSnackbar } from "notistack";
import { Helmet } from "react-helmet";

import { AddFab } from "../../components/common/FabButtuns";
import { ROLES } from "../../utils/constants";
import { createUser, updateUser, deleteUser } from "../../api/api";
import SearchFilter from "../../components/common/SearchFilter";
import Loader from "../../components/common/Loader";
import UsersTable from "../../components/features/tables/users";
import UserForm from "../../components/features/forms/user";
import { v4 as uuidv4 } from "uuid";
import { setDataList } from "../../store/action-reducers";
import { useDataLists } from "../../store/DataListsContextProvider";

const INIT_USER = {
  UserName: "",
  FirstName: "",
  LastName: "",
  Role: ROLES[0],
  Branch: "",
  Id: "",
  Password: "",
};

const Users = (props) => {
  const [open, setOpen] = useState(false);
  const { lists, dataListDispatch } = useDataLists();
  const [users, setUsers] = useState({
    filtered: [],
  });
  const [filterValue, setFilterValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [contextUser, setContextUser] = useState({
    current: {
      ...INIT_USER,
      Branch: lists.branches[0] ? lists.branches[0].Prefix : "",
      Id: uuidv4(),
    },
    changeType: "ADDITION",
  });

  useEffect(() => {
    if (filterValue) {
      setUsers({
        filtered: getFilteredUsers(filterValue, lists.users).sort(
          (a, b) => a.FirstName.charCodeAt(0) - b.FirstName.charCodeAt(0)
        ),
      });
    } else {
      setUsers({
        filtered: lists.users,
      });
    }
  }, [filterValue]);

  const handleOpenUserForm = (user) => {
    if (user.UserName) {
      setContextUser({
        current: user,
        changeType: "EDIT",
      });
    } else {
      setContextUser({
        current: {
          ...INIT_USER,
          Branch: lists.branches[0] ? lists.branches[0].Prefix : "",
          Id: uuidv4(),
        },
        changeType: "ADDITION",
      });
    }
    setOpen(true);
  };

  const handleSetContextUser = (user) => {
    setContextUser({
      current: user,
      changeType: "EDIT",
    });
  };

  const handleUserDataChange = (e) => {
    const value =
      e.target.value.length === 1 &&
      (e.target.name === "FirstName" || e.target.name === "LastName")
        ? e.target.value.toUpperCase()
        : e.target.value;

    setContextUser({
      ...contextUser,
      current: {
        ...contextUser.current,
        [e.target.name]: value,
      },
    });
  };

  const handleCloseUserForm = () => {
    setContextUser({
      current: {
        ...INIT_USER,
        Branch: lists.branches[0] ? lists.branches[0].Prefix : "",
        Id: uuidv4(),
      },
      changeType: "ADDITION",
    });
    setOpen(false);
  };

  const handleSaveUser = async () => {
    const userValues = Object.values(contextUser.current);

    if (userValues.includes("")) {
      props.enqueueSnackbar("ERROR : please fill in all fields.", {
        preventDuplicate: true,
        variant: "warning",
      });
      return;
    }

    setLoading(true);

    if (contextUser.changeType === "ADDITION") {
      const [, err] = await createUser(contextUser.current);

      if (!err) {
        let newUsersList = [...lists.users, contextUser.current].sort(
          (a, b) => a.FirstName.charCodeAt(0) - b.FirstName.charCodeAt(0)
        );

        console.log("newUsersList", newUsersList[newUsersList.length - 1]);

        setUsers({ filtered: newUsersList });
        dataListDispatch(setDataList(newUsersList, "users"));

        props.enqueueSnackbar("SUCCESS : user created.", {
          preventDuplicate: true,
          variant: "success",
        });
      } else {
        console.error("ERROR : ", err);
        props.enqueueSnackbar(`ERROR : ${err.message}`, {
          preventDuplicate: true,
          variant: "error",
        });
      }
    } else {
      const [_, err] = await updateUser(
        contextUser.current.Id,
        contextUser.current
      );

      if (!err) {
        let newUsersList = lists.users
          .map((user) => {
            if (user.Id === contextUser.current.Id) return contextUser.current;
            else return user;
          })
          .sort(
            (a, b) => a.FirstName.charCodeAt(0) - b.FirstName.charCodeAt(0)
          );

        setUsers({ filtered: newUsersList });
        dataListDispatch(setDataList(newUsersList, "users"));

        props.enqueueSnackbar("SUCCESS : user updated.", {
          preventDuplicate: true,
          variant: "success",
        });
      } else {
        console.error(err);
        props.enqueueSnackbar(`ERROR : ${err.message}`, {
          preventDuplicate: true,
          variant: "error",
        });
      }
    }

    setLoading(false);
    handleCloseUserForm();
  };

  const handleDeleteUser = async () => {
    const [_, err] = await deleteUser(contextUser.current.Id);
    if (!err) {
      let newUsersList = lists.users.filter(
        (user) => user.Id !== contextUser.current.Id
      );
      newUsersList = newUsersList.sort(
        (a, b) => a.FirstName.charCodeAt(0) - b.FirstName.charCodeAt(0)
      );

      setUsers({ filtered: newUsersList });
      dataListDispatch(setDataList(newUsersList, "users"));

      props.enqueueSnackbar("SUCCESS : user deleted.", {
        preventDuplicate: true,
        variant: "success",
      });
    } else {
      console.error(err);
      props.enqueueSnackbar(`ERROR : ${err.message}`, {
        preventDuplicate: true,
        variant: "error",
      });
    }
  };

  const handleFilterValueChange = (e) => {
    e.preventDefault();
    setFilterValue(e.target.value);
  };

  const getFilteredUsers = (filterString, users) => {
    const str = filterString.toLowerCase();
    if (!str) return users;
    else {
      return users.filter(
        (user) =>
          user.LastName.toLowerCase().includes(str) ||
          user.FirstName.toLowerCase().includes(str) ||
          user.UserName.toLowerCase().includes(str) ||
          user.Branch.toLowerCase().includes(str) ||
          user.Role.toLowerCase().includes(str)
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>Users</title>
      </Helmet>

      <SearchFilter
        handleChange={handleFilterValueChange}
        val={filterValue}
        placeholder={
          "Filter by 'ID', 'Firstname', 'Lastname', 'Username', 'Branch' or 'Role' "
        }
      />

      {loading ? (
        <Loader />
      ) : (
        <UsersTable
          users={users.filtered}
          user={contextUser.current}
          branches={lists.branches}
          handleChange={handleUserDataChange}
          handleOpenEditUserForm={handleOpenUserForm}
          handleSetContextUser={handleSetContextUser}
          save={handleSaveUser}
          handleDelete={handleDeleteUser}
        />
      )}

      {open && (
        <UserForm
          users={lists.users}
          user={contextUser.current}
          edit={contextUser.changeType === "EDIT"}
          close={handleCloseUserForm}
          save={handleSaveUser}
          handleChange={handleUserDataChange}
          branches={lists.branches}
          enqueueSnackbar={props.enqueueSnackbar}
        />
      )}

      <AddFab open={handleOpenUserForm} />
    </>
  );
};

export default withSnackbar(Users);
